import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { XIcon, ZoomInIcon, ZoomOutIcon } from 'lucide-react';
import { PendingArtwork } from './PendingArtwork';
export interface LightboxItem {
  image: {
    src: string;
    width: number;
    height: number;
  } | null;
  alt: string;
  caption: string;
}
interface PreviewLightboxProps {
  item: LightboxItem | null;
  onClose: () => void;
}
const EASE = [0.23, 1, 0.32, 1] as const;
const FOCUSABLE = 'button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])';
export function PreviewLightbox({
  item,
  onClose
}: PreviewLightboxProps) {
  const dialogRef = useRef<HTMLElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const [zoomed, setZoomed] = useState(false);
  useEffect(() => {
    setZoomed(false);
  }, [item?.caption]);
  useEffect(() => {
    if (!item) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== 'Tab' || !dialogRef.current) return;
      const focusables = Array.from(dialogRef.current.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;
      if (event.shiftKey && (active === first || !dialogRef.current.contains(active))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKey);
    };
  }, [item, onClose]);
  const buttonClass = 'inline-flex min-h-[44px] shrink-0 items-center justify-center gap-2 rounded-full px-4 text-sm font-bold transition-colors duration-150 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-teal focus-visible:ring-offset-2';
  return <AnimatePresence data-id="element-208" data-mp="3z85fkulua18">
      {item && <motion.div key="preview-lightbox" className="fixed inset-0 z-[60] flex items-center justify-center bg-brand-navy-deep/85 p-3 md:p-8" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2,
      ease: EASE
    }} onClick={onClose} data-id="element-209" data-mp="8zw5sx4q4384">
          <motion.figure ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby="preview-lightbox-caption" onClick={event => event.stopPropagation()} initial={{
        opacity: 0,
        scale: 0.96
      }} animate={{
        opacity: 1,
        scale: 1
      }} exit={{
        opacity: 0,
        scale: 0.96
      }} transition={{
        duration: 0.22,
        ease: EASE
      }} className="flex max-h-full w-full max-w-3xl flex-col rounded-2xl bg-white p-3 shadow-xl md:p-4" data-id="element-210" data-mp="mtwu0wy1u7u8">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-3 pl-1" data-id="element-211" data-mp="23kpl3857cbni">
              <figcaption id="preview-lightbox-caption" className="min-w-0 text-sm font-semibold text-brand-navy" data-id="element-212" data-mp="1zivqnhllo3vs">
                {item.caption}
              </figcaption>
              <div className="flex gap-2" data-id="element-213" data-mp="1bqniqr6lp33z">
                {item.image && <button type="button" onClick={() => setZoomed(value => !value)} aria-pressed={zoomed} className={`${buttonClass} bg-brand-ivory text-brand-navy hover:bg-brand-sand`} data-id="element-214" data-mp="16vrr3s5a0hre">
                    {zoomed ? <ZoomOutIcon className="h-4 w-4" aria-hidden="true" data-id="element-215" data-mp="s2o7zgc1dvyk" /> : <ZoomInIcon className="h-4 w-4" aria-hidden="true" data-id="element-216" data-mp="o1melg4swe4i" />}
                    {zoomed ? 'Réduire' : 'Agrandir'}
                  </button>}
                <button ref={closeRef} type="button" onClick={onClose} className={`${buttonClass} bg-brand-navy text-white hover:bg-brand-navy-deep`} data-id="element-217" data-mp="15peuqxfxr3j3">
                  <XIcon className="h-4 w-4" aria-hidden="true" data-id="element-218" data-mp="1khfai1uo1uf4" />
                  Fermer
                </button>
              </div>
            </div>
            <div tabIndex={0} aria-label="Zone de défilement de la page" className="min-h-0 flex-1 overflow-auto rounded-xl bg-brand-ivory focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-teal" data-id="element-219" data-mp="1okbzcaasumri">
              {item.image ? <img src={item.image.src} alt={item.alt} width={item.image.width} height={item.image.height} className={zoomed ? 'mx-auto h-auto w-[180%] max-w-none md:w-full' : 'mx-auto h-auto max-h-[calc(100svh-9rem)] w-auto max-w-full object-contain'} data-id="element-220" data-mp="3vb1qpizq7zpm" /> : <PendingArtwork label={`Chargement de l’extrait\u00A0: ${item.caption}`} className="mx-auto aspect-[9/16] max-h-[70svh] w-full max-w-sm rounded-xl" data-id="element-221" data-mp="3e0xscdmlanbf" />}
            </div>
          </motion.figure>
        </motion.div>}
    </AnimatePresence>;
}